import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GET_PATIENT_PROFILES, type PatientProfile } from '../../contracts';
import { deletePatientProfile } from '../api/patients';
import {
  readPatientDeleteRefusalReason,
  type PatientDeleteBeRefusalReason,
} from '../patient-delete-refusal-reason';

export interface DeletePatientProfileArgs {
  patientProfile: PatientProfile;
  /**
   * When `true` (default), the BE cascades and soft-deletes every session
   * linked to the patient. When `false`, the BE unlinks those sessions
   * instead — they survive in the session list with their patient
   * association cleared. The two-button force-pick modal sets this
   * explicitly; legacy callers that pass a bare `PatientProfile` (or
   * `{ patientProfile }` without the flag) keep the historical cascade.
   */
  deleteLinkedSessions?: boolean;
}

/**
 * The FIRST-PERSON half of `useMutateDeletePatientProfile` (D2.2, ARCFE-1053).
 *
 * `DELETE /patient-profiles/{id}` is patients'; `DELETE /teams/{teamId}/
 * members/{memberId}/patient-profiles/{id}` is workspace's and stayed in the
 * app hub with the review-mode branch. The two also differ in behaviour, which
 * is a second reason not to fuse them: only this endpoint honours
 * `deleteLinkedSessions` — the member endpoint always cascades server-side.
 *
 * `onDeleted` carries a cross-domain obligation, which is why it is a port and
 * not an internal call: either branch touches SESSIONS (cascade soft-deletes
 * them, unlink strips their `patient_profile_id`), so the session list must
 * refetch and the cached session-by-id snapshots must be cleared on the unlink
 * path. That cache is Session's; app integration sequences it (child design
 * D2). The port fires exactly where those reactions fired — after this
 * invalidation, before the toast — so the legacy order is preserved outright.
 *
 * `onDeleteBlocked` (COLAB-447) fires when the backend refused the delete with
 * a reason the dialog can explain, so the host flips its open dialog to the
 * blocked state. There is still no generic failure port: the legacy hook
 * logged and stopped, and a reasonless failure keeps doing exactly that.
 */
export function useMutateDeleteOwnPatientProfile({
  onDeleted,
  onDeleteBlocked,
}: {
  onDeleted?: (input: DeletePatientProfileArgs) => void;
  onDeleteBlocked?: (reason: PatientDeleteBeRefusalReason) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['DeletePatientProfile'],
    mutationFn: async ({
      patientProfile,
      deleteLinkedSessions = true,
    }: DeletePatientProfileArgs) => {
      if (patientProfile?.id) {
        return await deletePatientProfile(patientProfile.id, deleteLinkedSessions);
      }
      return Promise.reject(new Error('Patient profile ID is required'));
    },
    onSuccess: (_, input) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_PROFILES],
      });
      onDeleted?.(input);
    },
    onError: (error) => {
      // sanitizeError drops the AxiosError body/config so the patient id and
      // any backend-authored message body never reach Datadog.
      logger.error('delete_patient_profile_failed', sanitizeError(error));
      const reason = readPatientDeleteRefusalReason(error);
      if (reason != null) onDeleteBlocked?.(reason);
    },
  });
}
